import React, { useState } from "react"

import { addMonths, format } from "date-fns"
import { Loader2 } from "lucide-react"
import { CalendarIcon } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { NewOrientationEvent } from "@/hooks/useOrientationEvents"
import { cn } from "@/lib/utils"

interface CreateOrientationDialogProps {
  isLoading?: boolean
  error?: Error | null
  newEvent: NewOrientationEvent
  setNewEvent: React.Dispatch<React.SetStateAction<NewOrientationEvent>>
  handleCreateEvent: () => void
  jobPostings: { value: string; label: string }[]
  locations: { value: string; label: string }[]
}

const CreateOrientationDialog: React.FC<CreateOrientationDialogProps> = ({
  newEvent,
  setNewEvent,
  handleCreateEvent,
  jobPostings,
  locations,
  isLoading = false,
  error = null,
}) => {
  const [formErrors, setFormErrors] = useState<{
    title?: string
    jobPostings?: string
    date?: string
    startTime?: string
    endTime?: string
    locationKey?: string
    capacity?: string
  }>({})
  return (
    <DialogContent className="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>Create New Orientation Event</DialogTitle>
        <DialogDescription>
          Fill in the details to schedule a new orientation session.
        </DialogDescription>
      </DialogHeader>
      <div className="grid gap-4 py-4">
        {/* Title field */}
        <div className="grid gap-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={newEvent.title}
            onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
            placeholder="Enter event title"
          />
          {formErrors.title && <p className="text-xs text-destructive mt-1">{formErrors.title}</p>}
        </div>

        {/* Job Postings selection */}
        <div className="grid gap-2">
          <Label>Job Postings</Label>
          <MultiSelect
            options={jobPostings}
            selected={newEvent.selectedJobPostings}
            onChange={(selected) => setNewEvent({ ...newEvent, selectedJobPostings: selected })}
            placeholder="Select job postings..."
          />
          {newEvent.selectedJobPostings.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">Select at least one job posting</p>
          )}
        </div>

        {/* Date selection */}
        <div className="grid gap-2">
          <Label>Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(newEvent.date, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={newEvent.date}
                onSelect={(date) => date && setNewEvent({ ...newEvent, date })}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Time selection */}
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="startTime">Start Time</Label>
            <Input
              id="startTime"
              type="time"
              value={newEvent.startTime}
              onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="endTime">End Time</Label>
            <Input
              id="endTime"
              type="time"
              value={newEvent.endTime}
              onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })}
            />
          </div>
        </div>

        {/* Location selection */}
        <div className="grid gap-2">
          <Label htmlFor="location">Location</Label>
          <Select
            value={newEvent.locationKey}
            onValueChange={(value) => setNewEvent({ ...newEvent, locationKey: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {locations
                .filter((loc) => loc.value !== "all")
                .map((location) => (
                  <SelectItem key={location.value} value={location.value}>
                    {location.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        {/* Optional per-session address, for venues that aren't the depot */}
        <div className="grid gap-2">
          <Label htmlFor="locationAddress">Address (optional)</Label>
          <Input
            id="locationAddress"
            value={newEvent.locationAddress}
            onChange={(e) => setNewEvent({ ...newEvent, locationAddress: e.target.value })}
            placeholder="Leave blank to use the depot address"
          />
          <p className="text-xs text-muted-foreground">
            Overrides the depot address in the email sent to candidates. Applies to every session
            created, including recurrences.
          </p>
        </div>

        {/* Capacity */}
        <div className="grid gap-2">
          <Label htmlFor="capacity">Capacity</Label>
          <Input
            id="capacity"
            type="number"
            value={newEvent.capacity}
            onChange={(e) =>
              setNewEvent({
                ...newEvent,
                capacity: parseInt(e.target.value) || 0,
              })
            }
            min={1}
          />
        </div>

        {/* Recurrence pattern */}
        <div className="grid gap-2">
          <Label htmlFor="recurrence">Recurrence</Label>
          <Select
            value={newEvent.recurrence}
            onValueChange={(value: "none" | "daily" | "weekly" | "monthly") =>
              setNewEvent({
                ...newEvent,
                recurrence: value,
                recurrenceEndDate:
                  value === "none"
                    ? null
                    : newEvent.recurrenceEndDate || addMonths(newEvent.date, 1),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select recurrence pattern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Recurrence end date (only if recurrence is selected) */}
        {newEvent.recurrence !== "none" && (
          <div className="grid gap-2">
            <Label>Recurrence End Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {newEvent.recurrenceEndDate ? (
                    format(newEvent.recurrenceEndDate, "PPP")
                  ) : (
                    <span>Pick an end date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={newEvent.recurrenceEndDate || undefined}
                  onSelect={(date) => setNewEvent({ ...newEvent, recurrenceEndDate: date })}
                  disabled={(date) => date <= newEvent.date}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
      {error && (
        <div className="bg-destructive/15 text-destructive p-3 rounded-md text-sm mb-4">
          Failed to create orientation: {error.message || "Please try again"}
        </div>
      )}

      <DialogFooter>
        <Button
          onClick={() => {
            // Validate form
            const errors: Record<string, string> = {}
            if (!newEvent.title) {
              errors.title = "Enter a title for the event"
            }
            if (newEvent.selectedJobPostings.length === 0) {
              errors.jobPostings = "Select at least one job posting"
            }
            if (!newEvent.locationKey) {
              errors.locationKey = "Select a location"
            }
            if (!newEvent.startTime) {
              errors.startTime = "Enter a start time"
            }
            if (!newEvent.endTime) {
              errors.endTime = "Enter an end time"
            }
            if (parseInt(String(newEvent.capacity)) <= 0) {
              errors.capacity = "Capacity must be greater than 0"
            }

            setFormErrors(errors)

            if (Object.keys(errors).length === 0) {
              handleCreateEvent()
              toast.success("Creating orientation event", {
                description: "Your orientation event is being created...",
              })
            }
          }}
          disabled={
            isLoading ||
            !newEvent.title ||
            !newEvent.locationKey ||
            newEvent.selectedJobPostings.length === 0
          }
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            "Create Event"
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

export default CreateOrientationDialog
